import type { ListQuery } from '@alpha/api-client';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface TableQueryOptions {
  defaultPerPage?: number;
  defaultSort?: string;
  /** Extra param names this table reads off the URL (e.g. 'level', 'method'). */
  extraParams?: string[];
}

/**
 * The source app filters, sorts and paginates entirely server-side via URL
 * params (§6.4) — there is no client-side table state to keep in sync. This
 * hook makes the URL the single source of truth: TanStack Table runs in manual
 * mode and simply renders whatever the current query returned.
 */
export function useTableQuery(options: TableQueryOptions = {}) {
  const { defaultPerPage = 25, defaultSort, extraParams = [] } = options;
  const [searchParams, setSearchParams] = useSearchParams();

  const query = useMemo<ListQuery>(() => {
    const q: ListQuery = {
      page: Number(searchParams.get('page') ?? 1),
      per_page: Number(searchParams.get('per_page') ?? defaultPerPage),
    };
    const search = searchParams.get('q');
    if (search) q.q = search;

    const sort = searchParams.get('sort') ?? defaultSort;
    if (sort) {
      q.sort = sort;
      q.direction = (searchParams.get('direction') as 'asc' | 'desc') ?? 'desc';
    }

    for (const key of ['status', 'from', 'to', ...extraParams]) {
      const value = searchParams.get(key);
      if (value) q[key] = value;
    }
    return q;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, defaultPerPage, defaultSort, extraParams.join(',')]);

  const setParam = useCallback(
    (key: string, value: string | number | null | undefined) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value === null || value === undefined || value === '') next.delete(key);
          else next.set(key, String(value));
          // Any filter change invalidates the current page cursor.
          if (key !== 'page') next.delete('page');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setPage = useCallback((page: number) => setParam('page', page), [setParam]);

  const setSort = useCallback(
    (column: string) => {
      const isSame = query.sort === column;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('sort', column);
          next.set('direction', isSame && query.direction === 'desc' ? 'asc' : 'desc');
          next.delete('page');
          return next;
        },
        { replace: true },
      );
    },
    [query.sort, query.direction, setSearchParams],
  );

  return { query, setParam, setPage, setSort, page: query.page ?? 1 };
}
